import { Fragment } from 'react';

import {
  Navigate,
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import PublicChatBot from '../components/ChatBot/PublicChatBot';
import OAuthCallback from '../components/HubSpotIntegration/OAuthCallback';
import AuthLayout from '../components/Layout/AuthLayout';
import Layout from '../components/Layout/Layout';
import LayoutBeta from '../components/Layout/LayoutBeta';
import MicrosoftOAuthCallback from '../components/MicrosoftOAuth/MicrosoftOAuthCallback';
import McpOAuthCallback from '../components/ThirdPartyIntegration/oauth/McpOAuthCallback';
import ThirdPartyOAuthCallback from '../components/ThirdPartyIntegration/oauth/ThirdPartyOAuthCallback';
import AdminDashboard from '../views/AdminDashboard/AdminDashboard';
import AiModelsManagementPage from '../views/AdminDashboard/AiModelsManagementPage';
import CustomerUsagePage from '../views/AdminDashboard/CustomerPage';
import SpaceLoggingDetailPage from '../views/AdminDashboard/SpaceLogging/SpaceLoggingDetailPage';
import SpaceLoggingPage from '../views/AdminDashboard/SpaceLoggingPage';
import UserPlanManagementPage from '../views/AdminDashboard/UserPlanManagementPage';
import AdminRegister from '../views/AdminRegister/AdminRegister';
import CompanyAccept from '../views/Auth/CompanyAccept/CompanyAccept';
import ConfirmAccount from '../views/Auth/ConfirmAccount';
import ForgotPassword from '../views/Auth/ForgotPassword';
import Login from '../views/Auth/Login';
import Register from '../views/Auth/Register';
import ResetPassword from '../views/Auth/ResetPassword';
import Tapfiliate from '../views/Auth/Tapfiliate/Tapfiliate';
import CompanyUserLogin from '../views/CompanyUserLogin/CompanyUserLogin';
import CoreNodeElement from '../views/CoreNodeDisplay';
import CoreNodeDisplaySpaces from '../views/CoreNodeDisplaySpaces';
import Dashboard from '../views/Dashboard/Dashboard';
import DashboardBeta from '../views/DashboardBeta';
import AgentView from '../views/DashboardBeta/AgentView';
import DashboardHome from '../views/DashboardBeta/DashboardHome';
import InterfaceView from '../views/DashboardBeta/InterfaceView';
import FormBuilder from '../views/FormBuilder/FormBuilder';
import FormLayout from '../views/NewFormBuilder';
import NewPublishedForm from '../views/NewPublishedForm/PublishedForm';
import FulfillmentPolicy from '../views/PrivacyAndTerms/FulfillmentPolicy';
import Legal from '../views/PrivacyAndTerms/Legal';
import PrivacyPolicy from '../views/PrivacyAndTerms/PrivacyPolicy';
import TermsAndConditions from '../views/PrivacyAndTerms/TermsAndConditions';
import PublishedForm from '../views/PublishedForm/PublishedForm';
import Onboarding from '../views/SpecialRegister/Onboarding';
import SpecialRegister from '../views/SpecialRegister/SpecialRegister';
import Welcome from '../views/Welcome/Welcome';
import AdminRoute from './AdminRoute';
import InternalStaffRoute from './InternalStaffRoute';
import PrivateRoutes from './PrivateRoutes';
import PublicRoutes from './PublicRoutes';

const createPathRedirect = (
  replacements: Record<string, string> | ((pathname: string) => string),
) => {
  return () => {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const queryString = searchParams.toString();

    let redirectPath: string;

    if (typeof replacements === 'function') {
      redirectPath = replacements(location.pathname);
    } else {
      redirectPath = Object.entries(replacements).reduce(
        (path, [pattern, replacement]) => {
          const escapedPattern = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const regex = new RegExp(escapedPattern, 'g');
          return path.replace(regex, replacement);
        },
        location.pathname,
      );
    }

    const finalPath = redirectPath + (queryString ? `?${queryString}` : '');

    return <Navigate to={finalPath} replace />;
  };
};

const FlowToAgentRedirect = createPathRedirect({
  '/flow/': '/agent/',
  '/spaceFlow/': '/spaceAgent/',
});

const FormToInterfaceRedirect = createPathRedirect({
  '/form/': '/interface/',
});

export const Navigator = createBrowserRouter(
  createRoutesFromElements(
    <Fragment>
      <Route path='/' element={<PrivateRoutes />}>
        <Route path='/welcome' element={<Welcome />} />
        <Route path='/' element={<Layout />}>
          <Route path='/interface/:formId' element={<FormBuilder />} />
          <Route path='/v2/interface/:formId' element={<FormLayout />} />
          <Route path='/form/:formId' element={<FormToInterfaceRedirect />} />
          <Route
            path='/v2/form/:formId'
            element={<FormToInterfaceRedirect />}
          />
          <Route path='/agent/:flowId' element={<CoreNodeElement />} />
          <Route
            path='/spaceAgent/:flowId'
            element={<CoreNodeDisplaySpaces />}
          />
          <Route path='/flow/:flowId' element={<FlowToAgentRedirect />} />
          <Route path='/spaceFlow/:flowId' element={<FlowToAgentRedirect />} />
          <Route path='/' element={<Dashboard />} />
        </Route>
        <Route path='/dashboard-beta' element={<LayoutBeta />}>
          <Route index element={<DashboardHome />} />
          <Route
            path='/dashboard-beta/:dashboardId'
            element={<DashboardBeta />}
          />
          <Route path='/dashboard-beta/agent/:flowId' element={<AgentView />} />
          <Route
            path='/dashboard-beta/spaceAgent/:flowId'
            element={<AgentView />}
          />
          <Route
            path='/dashboard-beta/interface/:formId'
            element={<InterfaceView />}
          />
        </Route>
        <Route path='/admin' element={<AdminDashboard />} />
        <Route path='/oauth-callback/hubspot' element={<OAuthCallback />} />
        <Route
          path='/oauth-callback/microsoft'
          element={<MicrosoftOAuthCallback />}
        />
        <Route path='/mcp-oauth-callback' element={<McpOAuthCallback />} />
        <Route
          path='/oauth-callback/integration/:provider'
          element={<ThirdPartyOAuthCallback />}
        />
      </Route>
      <Route path='/admin/customer-usage' element={<CustomerUsagePage />} />
      <Route
        path='/admin/user-plan-management'
        element={<UserPlanManagementPage />}
      />
      <Route
        path='/admin/ai-models'
        element={
          <AdminRoute>
            <AiModelsManagementPage />
          </AdminRoute>
        }
      />
      <Route
        path='/admin/space-logging'
        element={
          <InternalStaffRoute>
            <SpaceLoggingPage />
          </InternalStaffRoute>
        }
      />
      <Route
        path='/admin/space-logging/:processId'
        element={
          <InternalStaffRoute>
            <SpaceLoggingDetailPage />
          </InternalStaffRoute>
        }
      />

      <Route path='/' element={<PublicRoutes />}>
        <Route path='/onboarding' element={<Onboarding />} />
        <Route path='/auth/register' element={<SpecialRegister />} />

        <Route path='/admin-register' element={<AdminRegister />} />
        <Route path='/company-user-login' element={<CompanyUserLogin />} />

        <Route path='/' element={<AuthLayout />}>
          <Route path='/login' element={<Login />} />
          <Route path='/register' element={<Register />} />
          <Route path='/forgot-password' element={<ForgotPassword />} />
          <Route path='/reset-password/:userId' element={<ResetPassword />} />
          <Route path='/confirm-account/:userId' element={<ConfirmAccount />} />
        </Route>
      </Route>

      <Route path='/company/accept-invite' element={<CompanyAccept />} />

      <Route path='/tapfiliate-invite' element={<Tapfiliate />} />
      <Route path='/published/interface/:formId' element={<PublishedForm />} />
      <Route
        path='/v2/published/interface/:formId'
        element={<NewPublishedForm />}
      />
      <Route
        path='/published/form/:formId'
        element={<FormToInterfaceRedirect />}
      />
      <Route
        path='/v2/published/form/:formId'
        element={<FormToInterfaceRedirect />}
      />
      <Route path='/chatbot/:chatId' element={<PublicChatBot />} />

      <Route path='/legal' element={<Legal />}>
        <Route
          path='/legal/terms-of-service'
          element={<TermsAndConditions />}
        />
        <Route path='/legal/privacy-policy' element={<PrivacyPolicy />} />
        <Route
          path='/legal/fulfillment-policy'
          element={<FulfillmentPolicy />}
        />
      </Route>
    </Fragment>,
  ),
);
